import React from 'react';

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg p-6 sm:p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Terms of Service</h1>

          <p className="text-gray-600 mb-6">Last updated: August 8, 2025</p>

          <div className="prose max-w-none">
            {/* Introduction */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Agreement to Terms</h2>
              <p className="text-gray-600 mb-4">
                These Terms of Service ("Terms") govern your access to and use of Postgame Buzz ("we," "us," or "our"). By accessing or using the services, you agree to be bound by these Terms. If you do not agree, do not use the services.
              </p>
              <p className="text-gray-600 mb-4">
                We serve users worldwide, with primary operations in the United States.
              </p>
            </section>

            {/* Eligibility */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Eligibility</h2>
              <p className="text-gray-600 mb-4">
                You must be at least 18 years old (or the age of majority in your jurisdiction) to create an account and use the services. The service is intended for adults such as parents and coaches. You are responsible for ensuring that any content you upload that includes minors is provided lawfully and with appropriate permissions.
              </p>
            </section>

            {/* Accounts */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Accounts and Security</h2>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li>You are responsible for all activity that occurs under your account and for maintaining the confidentiality of your login credentials.</li>
                <li>You agree to provide accurate information and to promptly update it as needed.</li>
                <li>We may suspend or terminate access for suspected fraud, abuse, or violations of these Terms.</li>
              </ul>
            </section>

            {/* User Content */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Content and License</h2>
              <p className="text-gray-600 mb-4">
                You retain ownership of the content you upload (including box scores, team and player details, and other materials). You grant us a worldwide, non‑exclusive, royalty‑free license to host, store, reproduce, process, adapt, and create derivative works from your content solely to operate, provide, improve, and support the services (including generating and streaming your podcasts).
              </p>
              <p className="text-gray-600 mb-4">
                You represent that you have all rights and permissions necessary to upload and use the content with our services, including any content involving minors, and that your content does not infringe or violate the rights of any third party or applicable law.
              </p>
            </section>

            {/* AI Output */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">AI‑Generated Content</h2>
              <p className="text-gray-600 mb-4">
                Our services use artificial intelligence to generate audio commentary. AI output can be unpredictable and may contain inaccuracies or content you did not intend. You are solely responsible for reviewing and using the output. The services and AI output are provided for entertainment purposes only and do not constitute advice of any kind.
              </p>
              <p className="text-gray-600 mb-4">
                We may remove or restrict content that we reasonably believe is unlawful, infringes rights, or violates these Terms.
              </p>
            </section>

            {/* Acceptable Use */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Acceptable Use</h2>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li>Do not upload content that is unlawful, defamatory, harassing, hateful, discriminatory, obscene, or otherwise objectionable.</li>
                <li>Do not attempt to reverse engineer, disrupt, or interfere with the services.</li>
                <li>Do not misrepresent the source of content or impersonate others.</li>
                <li>Do not use the services to collect or disclose sensitive personal information beyond what is reasonably necessary for generating podcasts.</li>
              </ul>
            </section>

            {/* Payments */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Payments and Credits</h2>
              <p className="text-gray-600 mb-4">
                Certain features require payment or credits processed by third‑party payment providers. We do not store full payment card details. Prices, features, and credit bundles may change from time to time. Unless otherwise stated, purchases are final and non‑refundable to the fullest extent permitted by law.
              </p>
            </section>

            {/* Intellectual Property */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Intellectual Property</h2>
              <p className="text-gray-600 mb-4">
                We own all rights in and to the services, including software, design, and trademarks. Except for your content and as expressly permitted, you may not copy, modify, distribute, or create derivative works from the services.
              </p>
            </section>

            {/* Termination */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Termination; Content Deletion</h2>
              <p className="text-gray-600 mb-4">
                You may stop using the services at any time. You can delete your podcasts in the product, which removes the database record and triggers deletion of the associated audio file from our cloud storage; if storage deletion fails due to a provider issue, we will continue attempts or apply safeguards. We may suspend or terminate the services or your access at any time for any reason, including for violations of these Terms.
              </p>
            </section>

            {/* Disclaimers */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Disclaimers</h2>
              <p className="text-gray-600 mb-4">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE SERVICES AND ALL CONTENT (INCLUDING AI‑GENERATED CONTENT) ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON‑INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED, ERROR‑FREE, OR SECURE.
              </p>
            </section>

            {/* Limitation of Liability */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Limitation of Liability</h2>
              <p className="text-gray-600 mb-4">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT WILL WE BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF (OR INABILITY TO USE) THE SERVICES OR CONTENT, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. OUR TOTAL LIABILITY FOR ANY CLAIMS ARISING OUT OF OR RELATED TO THE SERVICES WILL NOT EXCEED THE AMOUNTS YOU PAID TO US (IF ANY) FOR THE SERVICES IN THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM.
              </p>
            </section>

            {/* Indemnification */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Indemnification</h2>
              <p className="text-gray-600 mb-4">
                You agree to defend, indemnify, and hold harmless Postgame Buzz and our affiliates, officers, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable attorneys’ fees) arising out of or related to: (a) your content; (b) your use of the services; or (c) your violation of these Terms or applicable law.
              </p>
            </section>

            {/* Governing Law */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Governing Law and Venue</h2>
              <p className="text-gray-600 mb-4">
                These Terms are governed by the laws of the United States and the State of California, without regard to conflict‑of‑laws principles. You agree to the exclusive jurisdiction and venue of the state and federal courts located in California for any dispute that is not subject to arbitration or cannot be brought in small claims court.
              </p>
            </section>

            {/* Changes */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Changes to These Terms</h2>
              <p className="text-gray-600 mb-4">
                We may modify these Terms from time to time. We will post the updated Terms with the "Last updated" date above. Your continued use of the services after changes become effective constitutes your acceptance of the revised Terms.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
